import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';

import { fetchUploads } from '../Data/uploadsApi';
import { type UploadedFile } from '../Data/uploadedFiles';
import { classificationToRosterRow, type RosterRow } from '../Roster/rosterRows';
import {
  fetchRosterClassification,
  type RosterClassificationRow,
} from '../Roster/rosterClassification';
import { departedEmptyState, scopeByEnrollment } from '../Roster/enrollmentScope';
import { formatLastUpdated, latestUploadDate } from '../Roster/lastUpdated';
import {
  buildSchoolRows,
  EM_DASH,
  filterSchools,
  sortSchoolsByName,
} from './schoolRows';
import { SchoolsTable } from './SchoolsTable';
import { LoadFailure } from '../../components/LoadFailure';
import { SCHOOLS } from './tokens';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Props = {
  classificationSource?: () => Promise<RosterClassificationRow[] | null>;
  uploadsSource?: () => Promise<UploadedFile[]>;
};

function EmptyState({ onGoToData }: { onGoToData: () => void }) {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        sx={{ fontFamily: SCHOOLS.font, fontSize: 16, lineHeight: '24px', color: SCHOOLS.muted }}
      >
        No schools yet. Upload your district’s data to see your schools.
      </Typography>
      <Button
        onClick={onGoToData}
        sx={{ mt: 2, textTransform: 'none', fontFamily: SCHOOLS.font, fontWeight: 600 }}
      >
        Go to Data & Integrations
      </Button>
    </Box>
  );
}

function AllDepartedState() {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        data-testid="schools-all-departed"
        sx={{ fontFamily: SCHOOLS.font, fontSize: 16, lineHeight: '24px', color: SCHOOLS.ink }}
      >
        No active students in your most recent dataset.
      </Typography>
      <Typography
        sx={{ fontFamily: SCHOOLS.font, fontSize: 13, lineHeight: '20px', color: SCHOOLS.muted }}
      >
        The Students page can show who was here before.
      </Typography>
    </Box>
  );
}

export function SchoolsPage({
  classificationSource = fetchRosterClassification,
  uploadsSource = fetchUploads,
}: Props = {}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [rosterRows, setRosterRows] = useState<RosterRow[]>([]);

  useEffect(() => {
    let active = true;
    uploadsSource()
      .then((files) => {
        if (active) setLastUpdated(latestUploadDate(files));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [uploadsSource]);

  useEffect(() => {
    let active = true;
    classificationSource()
      .catch(() => null)
      .then((classified) => {
        if (!active) return;
        setFailed(classified === null);
        setRosterRows((classified ?? []).map(classificationToRosterRow));
        setHydrated(true);
      });
    return () => {
      active = false;
    };
  }, [classificationSource]);

  const rows = useMemo(
    () => sortSchoolsByName(buildSchoolRows(scopeByEnrollment(rosterRows.filter((row) => row.heldOut !== true), false))),
    [rosterRows],
  );
  const allDeparted = useMemo(
    () => departedEmptyState(rosterRows.filter((row) => row.school !== EM_DASH), false),
    [rosterRows],
  );
  const visibleRows = useMemo(() => filterSchools(rows, query), [rows, query]);

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SCHOOLS.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="schools" />
      <Box component="main" sx={{ flex: 1, p: '32px 24px 72px' }}>
        <Box
          data-testid="schools-card"
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            py: '24px',
            bgcolor: SCHOOLS.cardBg,
            borderRadius: '12px',
            boxShadow: SCHOOLS.cardShadow,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '16px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: SCHOOLS.font,
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: '36px',
                  color: SCHOOLS.ink,
                }}
              >
                Schools
              </Typography>
              {lastUpdated && (
                <Typography
                  sx={{
                    fontFamily: SCHOOLS.font,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '16px',
                    color: SCHOOLS.muted,
                  }}
                >
                  {`Last updated ${formatLastUpdated(lastUpdated)}`}
                </Typography>
              )}
            </Box>
            <TextField
              size="small"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: SCHOOLS.muted, width: 24, height: 24 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 264,
                '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px' },
              }}
            />
          </Box>
          {rows.length === 0 ? (
            !hydrated ? null : failed ? (
              <LoadFailure tokens={SCHOOLS} />
            ) : allDeparted ? (
              <AllDepartedState />
            ) : (
              <EmptyState onGoToData={() => navigate('/data')} />
            )
          ) : (
            <SchoolsTable
              rows={rows}
              visibleRows={visibleRows}
              onView={(name) => navigate(`/schools/${encodeURIComponent(name)}`)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
